import { uniq } from "lodash";
import type { VariableDB } from "@hightouch/lib/customer-data/decision-engine/types";
import { toAirshipVariableKey } from "./airship-variable-key";
import type { DecisionEngineDestinationDefinition } from "./definition";
import { getMappingForVariable } from "./sync-config-helpers";

/**
 * Sanitization is many-to-one ("Discount.Code" and "Discount Code" both map
 * to Discount_Code) and truncation to 32 characters can also collide;
 * colliding keys would silently overwrite one variable's substitution with
 * the other's. Config generation runs at enablement time, so throwing
 * surfaces the conflict to the user instead.
 */
function buildSubstitutionMappings(messageVariables: VariableDB[]) {
  const byKey = new Map<string, string>();
  for (const name of uniq(messageVariables.map((variable) => variable.name))) {
    const key = toAirshipVariableKey(name);
    const existing = byKey.get(key);
    if (existing !== undefined) {
      throw new Error(
        `Airship variables "${existing}" and "${name}" both map to substitution key "${key}"; rename one so the keys are distinct`,
      );
    }
    byKey.set(key, name);
  }
  return [...byKey.entries()].map(([key, name]) =>
    getMappingForVariable(name, key),
  );
}

/**
 * Airship template push sends (`POST /api/templates/push`) take a flat
 * key -> string substitutions object rather than a nested JSON payload, so
 * each AID message variable gets its own mapping instead of the usual single
 * `hightouch` blob.
 */
export const airshipDefinition: DecisionEngineDestinationDefinition = {
  destinationType: "airship",
  category: "trigger",
  generateSyncConfig: ({ messageVariables = [] }) => ({
    type: "trigger",
    templateId: { type: "standard", from: "campaign_id" },
    externalIdMapping: { from: "external_id", to: "named_user" },
    mappings: buildSubstitutionMappings(messageVariables),
  }),
};
